<template>
  <v-form ref="form" @submit.prevent="submit">
    <!-- Phone number card --->
    <div
      v-for="(_, index) in phoneNumbers"
      :key="index"
      :class="{ 'spacing-bottom--24': index !== phoneNumbers.length - 1 }">
      <v-card class="spacing-bottom--24">
        <v-card-title class="spacing-bottom--16">
          <span>{{ $t("resources.phone_numbers.create.phone") }} {{ index + 1 }}</span>
          <v-icon v-if="index !== 0" class="ml-auto" :size="18" color="error" @click="removePhoneNumber(index)">
            mdi-trash-can-outline</v-icon
          >
        </v-card-title>
        <va-autocomplete-input
          class="spacing-bottom--16"
          required
          v-model="phoneNumbers[index].domain"
          :label="$t('resources.phone_numbers.create.domain')"
          :placeholder="$t('resources.phone_numbers.create.domain_placeholder')"
          :choices="domainsList"
          :min-chars="1"
          validate-on-blur
          cache-items></va-autocomplete-input>
        <va-text-input
          v-model="phoneNumbers[index].publisherName"
          required
          class="spacing-bottom--16"
          :label="$t('resources.phone_numbers.create.publisher_name')"
          :placeholder="$t('resources.phone_numbers.create.publisher_name_placeholder')"></va-text-input>
        <va-text-input
          v-model="phoneNumbers[index].publisherSubId"
          required
          class="spacing-bottom--16"
          :label="$t('resources.phone_numbers.create.publisher_sub_id')"
          :placeholder="$t('resources.phone_numbers.create.publisher_sub_id_placeholder')"></va-text-input>
        <va-autocomplete-input
          class="spacing-bottom--16"
          required
          reference="callsrunner_campaigns"
          item-value="name"
          v-model="phoneNumbers[index].campaignName"
          :label="$t('resources.phone_numbers.create.campaign')"
          :placeholder="$t('resources.phone_numbers.create.campaign_placeholder')"
          :min-chars="0"
          showLoadMore
          validate-on-blur></va-autocomplete-input>
        <va-autocomplete-input
          class="spacing-bottom--16"
          required
          reference="callsrunner_number_pools"
          item-value="name"
          v-model="phoneNumbers[index].numberPoolName"
          :label="$t('resources.phone_numbers.create.number_pool')"
          :placeholder="$t('resources.phone_numbers.create.number_pool_placeholder')"
          :min-chars="0"
          custom-item
          showLoadMore
          validate-on-blur>
          <template #item="{ item, on }">
            <v-list-item v-on="on">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }} ({{ item.type }})</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </va-autocomplete-input>
        <va-select-input
          class="spacing-bottom--16"
          required
          v-model="phoneNumbers[index].countryCode"
          :label="$t('resources.phone_numbers.create.country')"
          :placeholder="$t('resources.phone_numbers.create.country_placeholder')"
          :choices="PHONE_NUMBER_COUNTRIES"></va-select-input>
        <va-select-input
          v-if="showCarrierSelect"
          class="spacing-bottom--16"
          required
          v-model="phoneNumbers[index].carrier"
          :label="$t('resources.phone_numbers.create.carrier')"
          :placeholder="$t('resources.phone_numbers.create.carrier_placeholder')"
          :choices="PHONE_NUMBER_CARRIERS"></va-select-input>
        <v-btn-toggle class="va-btn-toggle" v-model="phoneNumbers[index].type" mandatory tile color="primary" group>
          <v-btn :value="PHONE_NUMBER_TYPES.LOCAL">
            {{ $t("resources.phone_numbers.create.number_type_local") }}
          </v-btn>
          <v-btn :value="PHONE_NUMBER_TYPES.TOLL_FREE">
            {{ $t("resources.phone_numbers.create.number_type_toll_free") }}
          </v-btn>
        </v-btn-toggle>
        <va-text-input
          v-model="phoneNumbers[index].prefix"
          class="spacing-top--16"
          :label="$t('resources.phone_numbers.create.area_code')"
          :placeholder="$t('resources.phone_numbers.create.area_code_placeholder')"
          :rules="prefixRules"></va-text-input>
      </v-card>
    </div>
    <!-- Phone number card END --->

    <v-card>
      <v-card-title class="spacing-bottom--16">{{
        $t("resources.phone_numbers.create.add_phone_number")
      }}</v-card-title>

      <v-btn elevation="0" class="add-phone-number-btn" @click="addPhoneNumber">
        <v-icon class="mr-1" size="16">mdi-plus</v-icon>
        {{ $t("resources.phone_numbers.create.add_phone_number") }}
      </v-btn>
    </v-card>
  </v-form>
</template>

<script>
  // Mixins
  import crudFormMixin from "@/mixins/crudFormMixin";
  import ConnectedDomainsList from "@/mixins/connectedDomainsList";
  // Constants
  import {
    CALLSRUNNER_PHONE_NUMBER,
    PHONE_NUMBER_CARRIERS,
    PHONE_NUMBER_COUNTRIES,
    PHONE_NUMBER_TYPES,
  } from "@/constant";
  // Helpers
  import cloneDeep from "lodash/cloneDeep";
  import { formErrorMessage } from "@/utility/formErrorMessage";

  export default {
    name: "CallsRunnerPhoneNumberForm",
    mixins: [crudFormMixin, ConnectedDomainsList],
    data() {
      return {
        requiredFormFields: [],
        form: {},
        // CallsRunner only provisions telnyx numbers so far, so the choice stays hidden and the payload
        // carries the telnyx default from CALLSRUNNER_PHONE_NUMBER.
        showCarrierSelect: false,
        PHONE_NUMBER_CARRIERS,
        PHONE_NUMBER_COUNTRIES,
        PHONE_NUMBER_TYPES,
        phoneNumbers: [cloneDeep(CALLSRUNNER_PHONE_NUMBER)],
      };
    },
    beforeMount() {
      this.form.items = this.phoneNumbers;
      this.formOriginalState = JSON.stringify(this.form);
    },
    computed: {
      // CallsRunner treats the prefix as optional digits (max 10) for both local and toll free numbers,
      // unlike Ringba's toll-free-only 800-900 area code.
      prefixRules() {
        return [
          (v) => !v || /^\d+$/.test(v) || this.$t("resources.phone_numbers.create.area_code_numeric"),
          (v) => !v || v.length <= 10 || this.$t("resources.phone_numbers.create.prefix_length"),
        ];
      },
    },
    methods: {
      async removePhoneNumber(index) {
        if (
          await this.$admin.confirm(
            this.$t("resources.phone_numbers.create.delete_phone_number"),
            "",
            this.$t("overrides.confirm.delete_message"),
          )
        ) {
          this.phoneNumbers.splice(index, 1);
        }
      },
      addPhoneNumber() {
        const newPhoneNumber = {
          ...CALLSRUNNER_PHONE_NUMBER,
          domain: this.phoneNumbers[0].domain,
          campaignName: this.phoneNumbers[0].campaignName,
          numberPoolName: this.phoneNumbers[0].numberPoolName,
          countryCode: this.phoneNumbers[0].countryCode,
          carrier: this.phoneNumbers[0].carrier,
        };

        this.phoneNumbers.push(cloneDeep(newPhoneNumber));
      },
      async submit() {
        this.setSubmitAttempted();

        if (!this.$refs.form.validate()) {
          this.$admin.toast.error(this.$t("va.messages.required_fields_missing"));
          this.$emit("finishedSaving");

          return;
        }

        this.beforeSave();

        let response = null;

        try {
          response = await this.$admin.dataProvider.customHttpCall("POST", "phone_numbers/callsrunner", this.form);
        } catch (err) {
          this.$admin.toast.error(formErrorMessage(err, this.$t("generic.error")));
        } finally {
          this.isLoading = false;
          this.$emit("finishedSaving");
        }

        if (!response) {
          return;
        }

        // Drops the dirty state so the unsaved changes guard does not block the redirect.
        this.afterSave();
        this.$admin.toast.info(response.data.message);
        this.$router.push({ name: "phone_numbers_list" });
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .va-btn-toggle {
    border-radius: 4px;

    .v-btn {
      margin: 0;
      box-shadow: none !important;
      border: 1px solid get-color("secondary-btn");
      color: get-color("secondary-btn");
    }
  }

  .add-phone-number-btn {
    border-radius: 6px;
    padding: 8px;
    background-color: get-color("primary-10-hover") !important;
    border: 1px dashed get-color("primary-65") !important;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    color: get-color("secondary-btn") !important;
    @include text-button-medium();
  }
</style>
